import React from 'react';
import { FOREST_GREEN, WHITE } from '@/styles/tokens';

export type ActionButtonVariant = 'default' | 'active' | 'back' | 'destructive' | 'dashed';

interface ActionButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  variant?: ActionButtonVariant;
  title?: string;
}

const baseStyle: React.CSSProperties = {
  fontSize: '12px',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 150ms ease',
  fontWeight: 500,
};

const variantStyles: Record<ActionButtonVariant, React.CSSProperties> = {
  default: {
    ...baseStyle,
    padding: '6px 12px',
    border: '1px solid rgba(47, 133, 90, 0.3)',
    backgroundColor: 'rgba(47, 133, 90, 0.08)',
    color: FOREST_GREEN.DEFAULT,
  },
  active: {
    ...baseStyle,
    padding: '6px 12px',
    border: `1px solid ${FOREST_GREEN.DEFAULT}`,
    backgroundColor: 'rgba(47, 133, 90, 0.15)',
    color: FOREST_GREEN.DEFAULT,
    fontWeight: 600,
  },
  back: {
    ...baseStyle,
    padding: '2px 6px',
    background: 'none',
    border: 'none',
    color: FOREST_GREEN.DEFAULT,
  },
  destructive: {
    ...baseStyle,
    padding: '6px 12px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
    color: '#EF4444',
  },
  dashed: {
    ...baseStyle,
    padding: '6px 12px',
    border: '1px dashed rgba(47, 133, 90, 0.4)',
    backgroundColor: 'transparent',
    color: FOREST_GREEN.DEFAULT,
  },
};

const hoverHandlers: Record<ActionButtonVariant, {
  enter: (e: React.MouseEvent<HTMLButtonElement>) => void;
  leave: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = {
  default: {
    enter: (e) => {
      e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT;
      e.currentTarget.style.color = WHITE;
      e.currentTarget.style.borderColor = FOREST_GREEN.DEFAULT;
    },
    leave: (e) => {
      e.currentTarget.style.backgroundColor = 'rgba(47, 133, 90, 0.08)';
      e.currentTarget.style.color = FOREST_GREEN.DEFAULT;
      e.currentTarget.style.borderColor = 'rgba(47, 133, 90, 0.3)';
    },
  },
  active: {
    enter: (e) => {
      e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT;
      e.currentTarget.style.color = WHITE;
      e.currentTarget.style.borderColor = FOREST_GREEN.DEFAULT;
    },
    leave: (e) => {
      e.currentTarget.style.backgroundColor = 'rgba(47, 133, 90, 0.15)';
      e.currentTarget.style.color = FOREST_GREEN.DEFAULT;
      e.currentTarget.style.borderColor = FOREST_GREEN.DEFAULT;
    },
  },
  back: {
    enter: () => {},
    leave: () => {},
  },
  destructive: {
    enter: (e) => {
      e.currentTarget.style.backgroundColor = '#EF4444';
      e.currentTarget.style.color = WHITE;
      e.currentTarget.style.borderColor = '#EF4444';
    },
    leave: (e) => {
      e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
      e.currentTarget.style.color = '#EF4444';
      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
    },
  },
  dashed: {
    enter: (e) => {
      e.currentTarget.style.borderColor = FOREST_GREEN.DEFAULT;
      e.currentTarget.style.backgroundColor = 'rgba(47, 133, 90, 0.08)';
    },
    leave: (e) => {
      e.currentTarget.style.borderColor = 'rgba(47, 133, 90, 0.4)';
      e.currentTarget.style.backgroundColor = 'transparent';
    },
  },
};

export const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  onClick,
  variant = 'default',
  title,
}) => {
  const safeVariant = variant in variantStyles ? variant : 'default';
  const handlers = hoverHandlers[safeVariant];
  return (
    <button
      onClick={onClick}
      title={title}
      style={variantStyles[safeVariant]}
      onMouseEnter={handlers.enter}
      onMouseLeave={handlers.leave}
    >
      {children}
    </button>
  );
};
